import { DprTableOfColumnsAccumulation } from '@dpr-details/models';
import { DprTableOfColumnDetail } from '@shared/models/dpr';
import {
  StatusTrackerColumn,
  StatusTrackerColumnCarryOver,
  StatusTrackerWithData,
  TableOfColumnItemWithValues,
  TableOfColumnWithData,
} from '@shared/models/project-setup';

/** Where a carry-over starts when it has nothing to read and no initial value. */
export const CarryOverDefaultValue = '0';
// The largest figure the API carries is ±9,999,999,999,999,999.99. It reads a
// figure that size to 12 decimals, rounding an exact half to even, so it still
// carries anything up to half a unit in the 12th decimal past it.
const MaxCarriedFigure = '9999999999999999.9900000000005';

// The forms the API carries as a figure: a sign and a decimal point, but no
// thousands separator and no exponent.
const CarriedFigurePattern = /^[+-]?(\d+\.?\d*|\.\d+)$/;
// The API accepts only spaces, tabs and line breaks around a figure, so a figure
// padded with a non-breaking space carries blank.
const CarriedFigurePadding = /^[\t\n\v\f\r ]+|[\t\n\v\f\r ]+$/g;
// An anchor cell names an option only when it holds a whole number, as the API
// reads it: `7abc` or `7.9` names none.
const AnchorOptionIdPattern = /^\s*[+-]?\d+\s*$/;

/**
 * A carry-over record stays on the column with `carryOverEnabled: false` once the
 * user turns it off, so its presence alone says nothing about whether the column
 * is accumulated.
 */
export function isTableOfColumnItemCarryOverEnabled(
  item: TableOfColumnItemWithValues
): boolean {
  return !!item?.carryOver?.carryOverEnabled;
}

/**
 * The project setup response carries the Status Tracker carry-overs both nested on
 * each column and flattened on the status tracker, so fall back to the flat list
 * rather than treating a missing nested record as "not accumulated".
 */
export function getStatusTrackerColumnCarryOver(
  statusTracker: StatusTrackerWithData,
  column: StatusTrackerColumn
): StatusTrackerColumnCarryOver | undefined {
  if (column?.carryOver) {
    return column.carryOver;
  }

  return statusTracker?.carryOvers?.find(
    (carryOver) => +carryOver.statusTrackerColumnId === +column?.id
  );
}

export function isStatusTrackerColumnCarryOverEnabled(
  statusTracker: StatusTrackerWithData,
  column: StatusTrackerColumn
): boolean {
  return !!getStatusTrackerColumnCarryOver(statusTracker, column)
    ?.carryOverEnabled;
}

/**
 * The list option id an anchor cell holds, or null when it names none. Carry-overs
 * and formulas both read a row's option through this, so they always match a row
 * to the same option.
 */
export function parseAnchorOptionId(value: unknown): number | null {
  const text = String(value);

  return AnchorOptionIdPattern.test(text) ? parseInt(text, 10) : null;
}

/**
 * Normalises an anchor cell into the key used by
 * `DprTableOfColumnsAccumulation.valuesByAnchorValue`. A row with no anchor value,
 * or one that names no option, cannot be matched to a row in another DPR, so it
 * resolves to null.
 */
export function normalizeAnchorValue(value: string | number): string | null {
  const optionId = parseAnchorOptionId(value);

  return optionId === null ? null : String(optionId);
}

export function getRowAnchorValue(
  detail: DprTableOfColumnDetail,
  anchorColumnId: number
): string | null {
  return normalizeAnchorValue(
    detail?.dprTableOfColumnDetailItems?.find(
      (detailItem) => detailItem.tableOfColumnItemId === anchorColumnId
    )?.value
  );
}

/**
 * Hidden rows stay in the DPR and are still saved and synced; only the grid
 * leaves them out. A missing flag counts as active, so a project setup cached
 * before the flag existed hides no row.
 */
export function withoutInactiveAnchorRows(
  details: DprTableOfColumnDetail[],
  tableOfColumn: TableOfColumnWithData
): DprTableOfColumnDetail[] {
  const anchorColumnId = tableOfColumn?.anchorColumn?.anchorColumnId;

  if (!details?.length || anchorColumnId == null) {
    return details;
  }

  const inactiveAnchorValues = new Set(
    tableOfColumn.tableOfColumnItems
      ?.find((item) => +item.id === +anchorColumnId)
      ?.values?.filter((value) => value.isActive === false)
      .map((value) => String(value.id))
  );

  if (!inactiveAnchorValues.size) {
    return details;
  }

  return details.filter((detail) => {
    const anchorValue = getRowAnchorValue(detail, +anchorColumnId);

    return anchorValue === null || !inactiveAnchorValues.has(anchorValue);
  });
}

/**
 * The value a source cell carries over with, as the API carries it: anything that
 * isn't a figure in range carries blank, a formula's division-by-zero error
 * included.
 */
export function normalizeCarryOverValue(value: string | number): string | null {
  const text = value?.toString().replace(CarriedFigurePadding, '') ?? '';

  // Kept as written, so a formula source's 10 decimals reach the formulas reading
  // the carried value; only the display rounds it.
  return CarriedFigurePattern.test(text) && isInCarriedRange(text)
    ? text.replace(/^\+/, '')
    : null;
}

/**
 * Compares the written digits as whole numbers, since a JavaScript number can't
 * tell the limit from 10,000,000,000,000,000. Expects a figure: anything else
 * throws.
 */
function isInCarriedRange(figure: string): boolean {
  const [integerDigits, fractionDigits = ''] = figure
    .replace(/^[+-]/, '')
    .split('.');
  const [maxIntegerDigits, maxFractionDigits] = MaxCarriedFigure.split('.');
  const decimals = Math.max(fractionDigits.length, maxFractionDigits.length);
  const toScaled = (integer: string, fraction: string): bigint =>
    BigInt((integer || '0') + fraction.padEnd(decimals, '0'));

  return (
    toScaled(integerDigits, fractionDigits) <=
    toScaled(maxIntegerDigits, maxFractionDigits)
  );
}

/** The carry-over item id → value map of the row identified by the given anchor value. */
function getCarryOverValues(
  accumulation: DprTableOfColumnsAccumulation,
  anchorValue: string | null
): Map<number, string | null> | undefined {
  return anchorValue == null
    ? undefined
    : accumulation?.valuesByAnchorValue.get(anchorValue);
}

/**
 * Overwrites the row's accumulated cells with the values resolved for its anchor
 * value, and clears the ones it has none for. Whatever a cell held is replaced on
 * purpose: a value it arrived with, or the previous option's after an anchor
 * change, doesn't describe the DPR before as this row reads it.
 *
 * The caller supplies the setter because building a row and editing one already on
 * screen have to write the cell differently.
 */
export function applyCarryOverValues(
  accumulation: DprTableOfColumnsAccumulation,
  anchorValue: string | null,
  setValue: (itemId: number, value: string | null) => void
): void {
  const carryOverValues = getCarryOverValues(accumulation, anchorValue);

  accumulation.carryOverItemIds.forEach((itemId) =>
    setValue(itemId, carryOverValues?.get(itemId) ?? null)
  );
}
